import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { createRouteConfig } from 'modules/router/utils/createRouteConfig';
import { useQueryParams } from 'modules/common/hooks/useQueryParams';

const TAB_QUERY = 'tab';

const STATUS_PATH = '/health/';

interface StatusRouteParams {
  tab?: string;
}

export interface StatusRouteHookResult {
  tab?: string;
  setTab: (tabValue: string) => void;
  removeTab: () => void;
}

const generatePath = (params?: StatusRouteParams) => {
  if (!params?.tab) {
    return STATUS_PATH;
  }

  return `${STATUS_PATH}?${TAB_QUERY}=${params.tab}`;
};

export const statusRoutesConfig = createRouteConfig(
  {
    status: {
      path: STATUS_PATH,
      generatePath,
      useParams: (): StatusRouteHookResult => {
        const navigate = useNavigate();
        const query = useQueryParams();

        const tab = query.get(TAB_QUERY) || undefined;

        const setTab = useCallback(
          (tabValue: string) => {
            const newQuery = new URLSearchParams(query);

            newQuery.set(TAB_QUERY, tabValue);

            navigate({ search: newQuery.toString() }, { replace: true });
          },
          [navigate, query],
        );

        const removeTab = useCallback(() => {
          const newQuery = new URLSearchParams(query);

          newQuery.delete(TAB_QUERY);

          const searchString = newQuery.toString();

          navigate(
            { search: searchString ? `?${searchString}` : '' },
            { replace: true },
          );
        }, [navigate, query]);

        return { tab, setTab, removeTab };
      },
    },
  },
  STATUS_PATH,
);
